import { ArrowLeft, Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import LoadingCircle from '../shared/LoadingCircle';
import Translate from '../shared/Translate';
import { errorLogEnabled, isPhoneNumberValid } from '../shared/Utils';
import '../styles/settings.css';

const Settings = (props) => {
  const [activeTab, setActiveTab] = useState('calls');
  const [availableLines, setAvailableLines] = useState(undefined);
  const [availableDevices, setAvailableDevices] = useState(undefined);
  const [notificationDisplayTime, setNotificationDisplayTime] = useState(props.settingsData.get('.notificationDisplayTime') || 30);

  const handleLogout = () => {
    props.container.tell({
      _class: 'com.optimsys.costra.optimcall.pipedrive.user.CurrentUserInfo$Logout',
    })
  }

  const updateSettings = (path, value) => {
    props.settingsData.set(path, value)
    props.container.tell({
      _class: 'com.optimsys.costra.optimcall.pipedrive.user.CurrentUserInfo$UpdateSettings',
      settings: props.settingsData.get('')
    })
  }

  const toggleCreateActivityForDone = (path) => {
    const currentActivityValue = props.settingsData.get(path)
    if (!currentActivityValue || currentActivityValue.markAsDone) {
      updateSettings(path, { markAsDone: false })
    }
    else {
      updateSettings(path, { markAsDone: true })
    }
  }

  useEffect(() => {
    props.container.ask({
      _class: 'com.optimsys.costra.optimcall.pipedrive.user.CurrentUserInfo$GetUserLinesAndDevices'
    }).then((response) => {
      if (response) {
        setAvailableLines(
          (response.availableIdentifications || []).map(line => ({ ...line, isIdentification: true })).concat(
            (response.availableLines || []).map(line => ({ ...line, isIdentification: false })))
        );
        setAvailableDevices(response.availableDevices);
      } else {
        setAvailableLines([]);
        setAvailableDevices([]);
      }
    }).catch((error) => {
      errorLogEnabled && console.error('Failed to fetch available lines and devices:', error);
    });
  }, [props.credentials, props.container]);

  return (
    <div className="settings-container">
      <div className="settings-card">
        <div
          className="settings-header"
          onClick={props.onClose}
        >
          <div className="settings-back-icon">
            <ArrowLeft className="settings-back-icon" />
          </div>
          <h2 className="settings-title">
            {Translate.get('settings.title')}
          </h2>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`tab-button ${activeTab === 'calls' ? 'active' : ''}`}
            onClick={() => setActiveTab('calls')}
          >
            {Translate.get('settings.calls')}
          </button>
          {props.isSmsSupported && <button
            className={`tab-button ${activeTab === 'sms' ? 'active' : ''}`}
            onClick={() => setActiveTab('sms')}
          >
            {Translate.get('settings.sms')}
          </button>}
          <button
            className={`tab-button ${activeTab === 'other' ? 'active' : ''}`}
            onClick={() => setActiveTab('other')}
          >
            {Translate.get('settings.other')}
          </button>
          <button
            className={`tab-button ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            {Translate.get('settings.about')}
          </button>
        </div>

        {/* Tab Content */}
        <div className="settings-content">
          {activeTab === 'calls' && (
            availableLines ? (<div>
              <div className="settings-section">
                <div className="settings-section-header">
                  <div className="settings-info">
                    <div className="settings-name">
                      {Translate.get('settings.calls.createCallActivityAs')}
                    </div>
                  </div>
                </div>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="createCallActivityAs"
                      value="CallLog"
                      checked={(props.settingsData.get('.callSettings.createCallActivityAs') === 'CallLog') ||
                        !props.settingsData.get('.callSettings.createCallActivityAs')}
                      onChange={(event) => updateSettings('.callSettings.createCallActivityAs', event.target.value)}
                    />
                    {Translate.get('settings.calls.callLogActivity')}
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="createCallActivityAs"
                      value="CallLogWithRecording"
                      checked={props.settingsData.get('.callSettings.createCallActivityAs') === 'CallLogWithRecording'}
                      onChange={(event) => updateSettings('.callSettings.createCallActivityAs', event.target.value)}
                    />
                    {Translate.get('settings.calls.callLogActivityWithRecording')}
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="createCallActivityAs"
                      value="StandardCallActivity"
                      checked={props.settingsData.get('.callSettings.createCallActivityAs') === 'StandardCallActivity'}
                      onChange={(event) => updateSettings('.callSettings.createCallActivityAs', event.target.value)}
                    />
                    {Translate.get('settings.calls.standardCallActivity')}
                  </label>
                </div>
              </div>

              {availableLines.map((line, index) => (
                <div key={`line-${index}`} className="settings-section">
                  <div className="settings-section-header">
                    <div className="settings-info">
                      {line.title
                        ? <div className="settings-name">{line.phoneNumber} - {line.title}</div>
                        : <div className="settings-name">{line.phoneNumber}</div>
                      }
                    </div>
                    <label className="switch-container">
                      <input
                        type="checkbox"
                        className="switch-input"
                        checked={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                        onChange={() => {
                          const currentEnabled = props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                          props.settingsData.set(`.callSettings.lineSettings.${line.phoneNumber}.title`, line.title || '')
                          props.settingsData.set(`.callSettings.lineSettings.${line.phoneNumber}.isIdentification`, line.isIdentification || false)
                          updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`, !currentEnabled)
                        }}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>

                  <div className="notification-group-label">
                    {Translate.get('settings.calls.notificationsFor')}
                  </div>
                  <div className="notification-group">
                    <div className="notification-type">
                      {Translate.get('settings.calls.inbound')}
                    </div>
                    <div className="button-group">
                      <button
                        disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                        className={'option-button ' +
                          ((props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.inboundNotificationsFor`) === 'CallDetail' ||
                            !props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.inboundNotificationsFor`))
                            ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                            updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.inboundNotificationsFor`, 'CallDetail')
                        }}
                      >
                        {Translate.get('settings.calls.callDetail')}
                      </button>
                      <button
                        disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                        className={'option-button ' +
                          (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.inboundNotificationsFor`) === 'MainPageOnly'
                            ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                            updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.inboundNotificationsFor`, 'MainPageOnly')
                        }}
                      >
                        {Translate.get('settings.calls.mainPageOnly')}
                      </button>
                      <button
                        disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                        className={'option-button ' +
                          (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.inboundNotificationsFor`) === 'Off'
                            ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                            updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.inboundNotificationsFor`, 'Off')
                        }}
                      >
                        {Translate.get('settings.calls.off')}
                      </button>
                    </div>
                  </div>

                  {!isPhoneNumberValid(line.phoneNumber) && (
                    <div className="notification-group">
                      <div className="notification-type">
                        {Translate.get('settings.calls.outbound')}
                      </div>
                      <div className="button-group">
                        <button
                          disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                          className={'option-button ' +
                            ((props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.outboundNotificationsFor`) === 'CallDetail' ||
                              !props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.outboundNotificationsFor`))
                              ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                                ? 'selected'
                                : 'selected-silent')
                              : '')
                          }
                          onClick={() => {
                            props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                              updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.outboundNotificationsFor`, 'CallDetail')
                          }}
                        >
                          {Translate.get('settings.calls.callDetail')}
                        </button>
                        <button
                          disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                          className={'option-button ' +
                            (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.outboundNotificationsFor`) === 'MainPageOnly'
                              ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                                ? 'selected'
                                : 'selected-silent')
                              : '')
                          }
                          onClick={() => {
                            props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                              updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.outboundNotificationsFor`, 'MainPageOnly')
                          }}
                        >
                          {Translate.get('settings.calls.mainPageOnly')}
                        </button>
                        <button
                          disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                          className={'option-button ' +
                            (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.outboundNotificationsFor`) === 'Off'
                              ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                                ? 'selected'
                                : 'selected-silent')
                              : '')
                          }
                          onClick={() => {
                            props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                              updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.outboundNotificationsFor`, 'Off')
                          }}
                        >
                          {Translate.get('settings.calls.off')}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="notification-group-label">
                    {Translate.get('settings.calls.createCallActivityFor')}
                  </div>
                  <div className="notification-group">
                    <div className="activities-grid">
                      <div
                        className={'activity-item' + (!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) ? ' disabled' : '')}
                      >
                        <div className='activity-control'>
                          <label className="switch-container small">
                            <input
                              id={`${line.phoneNumber}_inboundUnanswered`}
                              type="checkbox"
                              className='switch-input'
                              checked={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered`)}
                              disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                              onChange={() => {
                                if (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)) {
                                  const value = props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered`)
                                  if (value) {
                                    updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered`, undefined)
                                  }
                                  else {
                                    updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered`, { markAsDone: false })
                                  }
                                }
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <label
                            htmlFor={`${line.phoneNumber}_inboundUnanswered`}
                            className="activity-label"
                            disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                          >
                            {Translate.get('settings.calls.inboundUnanswered')}
                          </label>
                        </div>

                        {(props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered`)) &&

                          <div
                            className='activity-control'
                            onClick={() => props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                              toggleCreateActivityForDone(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered`)}
                          >
                            <div className={'checkbox ' +
                              (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered.markAsDone`)
                                ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                                  ? 'checked'
                                  : 'checked-silent')
                                : 'unchecked')
                            }>
                              {props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered.markAsDone`) &&
                                <Check size={14} color={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundUnanswered.markAsDone`) ? 'white' : '#a5a5a5'} />
                              }
                            </div>
                            <label className="activity-label extended">
                              {Translate.get('settings.calls.markAsDone')}
                            </label>
                          </div>
                        }
                      </div>
                      <div
                        className={'activity-item' + (!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) ? ' disabled' : '')}
                      >
                        <div className='activity-control'>
                          <label className="switch-container small">
                            <input
                              id={`${line.phoneNumber}_inboundAnswered`}
                              type="checkbox"
                              className='switch-input'
                              checked={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered`)}
                              disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                              onChange={() => {
                                if (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)) {
                                  const value = props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered`)
                                  if (value) {
                                    updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered`, undefined)
                                  }
                                  else {
                                    updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered`, { markAsDone: true })
                                  }
                                }
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <label
                            htmlFor={`${line.phoneNumber}_inboundAnswered`}
                            className="activity-label"
                            disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                          >
                            {Translate.get('settings.calls.inboundAnswered')}
                          </label>
                        </div>

                        {(props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered`)) &&

                          <div
                            className='activity-control'
                            onClick={() => props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                              toggleCreateActivityForDone(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered`)}
                          >
                            <div className={'checkbox ' +
                              (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered.markAsDone`)
                                ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                                  ? 'checked'
                                  : 'checked-silent')
                                : 'unchecked')
                            }>
                              {props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered.markAsDone`) &&
                                <Check size={14} color={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.inboundAnswered.markAsDone`) ? 'white' : '#a5a5a5'} />
                              }
                            </div>
                            <label className="activity-label extended">
                              {Translate.get('settings.calls.markAsDone')}
                            </label>
                          </div>
                        }
                      </div>
                      {!isPhoneNumberValid(line.phoneNumber) &&
                        <div
                          className={'activity-item' + (!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) ? ' disabled' : '')}
                        >
                          <div className='activity-control'>
                            <label className="switch-container small">
                              <input
                                id={`${line.phoneNumber}_outboundUnanswered`}
                                type="checkbox"
                                className='switch-input'
                                checked={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered`)}
                                disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                                onChange={() => {
                                  if (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)) {
                                    const value = props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered`)
                                    if (value) {
                                      updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered`, undefined)
                                    }
                                    else {
                                      updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered`, { markAsDone: false })
                                    }
                                  }
                                }}
                              />
                              <span className="switch-slider"></span>
                            </label>
                            <label
                              htmlFor={`${line.phoneNumber}_outboundUnanswered`}
                              className="activity-label"
                              disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                            >
                              {Translate.get('settings.calls.outboundUnanswered')}
                            </label>
                          </div>

                          {(props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered`)) &&

                            <div
                              className='activity-control'
                              onClick={() => props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                                toggleCreateActivityForDone(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered`)}
                            >
                              <div className={'checkbox ' +
                                (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered.markAsDone`)
                                  ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                                    ? 'checked'
                                    : 'checked-silent')
                                  : 'unchecked')
                              }>
                                {props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered.markAsDone`) &&
                                  <Check size={14} color={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundUnanswered.markAsDone`) ? 'white' : '#a5a5a5'} />
                                }
                              </div>
                              <label className="activity-label extended">
                                {Translate.get('settings.calls.markAsDone')}
                              </label>
                            </div>
                          }
                        </div>
                      }
                      {!isPhoneNumberValid(line.phoneNumber) &&
                        <div
                          className={'activity-item' + (!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) ? ' disabled' : '')}
                        >
                          <div className='activity-control'>
                            <label className="switch-container small">
                              <input
                                id={`${line.phoneNumber}_outboundAnswered`}
                                type="checkbox"
                                className='switch-input'
                                checked={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered`)}
                                disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                                onChange={() => {
                                  if (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)) {
                                    const value = props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered`)
                                    if (value) {
                                      updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered`, undefined)
                                    }
                                    else {
                                      updateSettings(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered`, { markAsDone: true })
                                    }
                                  }
                                }}
                              />
                              <span className="switch-slider"></span>
                            </label>
                            <label
                              htmlFor={`${line.phoneNumber}_outboundAnswered`}
                              className="activity-label"
                              disabled={!props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)}
                            >
                              {Translate.get('settings.calls.outboundAnswered')}
                            </label>
                          </div>

                          {(props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered`)) &&

                            <div
                              className='activity-control'
                              onClick={() => props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`) &&
                                toggleCreateActivityForDone(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered`)}
                            >
                              <div className={'checkbox ' +
                                (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered.markAsDone`)
                                  ? (props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.isEnabled`)
                                    ? 'checked'
                                    : 'checked-silent')
                                  : 'unchecked')
                              }>
                                {props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered.markAsDone`) &&
                                  <Check size={14} color={props.settingsData.get(`.callSettings.lineSettings.${line.phoneNumber}.createCallActivityFor.outboundAnswered.markAsDone`) ? 'white' : '#a5a5a5'} />
                                }
                              </div>
                              <label className="activity-label extended">
                                {Translate.get('settings.calls.markAsDone')}
                              </label>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>) : <LoadingCircle />
          )}

          {props.isSmsSupported && activeTab === 'sms' && (
            availableDevices ? (<div>
              {availableDevices.map((device, index) => (
                <div key={`device-${index}`} className="settings-section">
                  <div className="settings-section-header">
                    <div className="settings-info">
                      <div className="settings-name">{device.displayName}</div>
                    </div>
                    <label className="switch-container">
                      <input
                        type="checkbox"
                        className="switch-input"
                        checked={props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                        onChange={() => {
                          const currentEnabled = props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`);
                          updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`, !currentEnabled);
                          // update display name if the device was not enabled
                          !currentEnabled && updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.displayName`, device.displayName);
                        }}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>

                  <div className="notification-group-label">
                    {Translate.get('settings.sms.notificationsFor')}
                  </div>
                  <div className="notification-group">
                    <div className="notification-type">
                      {Translate.get('settings.sms.inbound')}
                    </div>
                    <div className="button-group">
                      <button
                        disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                        className={'option-button ' +
                          ((props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.inboundNotificationsFor`) === 'SmsDetail')
                            ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                            updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.inboundNotificationsFor`, 'SmsDetail')
                        }}
                      >
                        {Translate.get('settings.sms.smsDetail')}
                      </button>
                      <button
                        disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                        className={'option-button ' +
                          ((props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.inboundNotificationsFor`) === 'MainPageOnly' ||
                            !props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.inboundNotificationsFor`))
                            ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                            updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.inboundNotificationsFor`, 'MainPageOnly')
                        }}
                      >
                        {Translate.get('settings.sms.mainPageOnly')}
                      </button>
                      <button
                        disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                        className={'option-button ' +
                          (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.inboundNotificationsFor`) === 'Off'
                            ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                            updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.inboundNotificationsFor`, 'Off')
                        }}
                      >
                        {Translate.get('settings.sms.off')}
                      </button>
                    </div>
                  </div>

                  <div className="notification-group">
                    <div className="notification-type">
                      {Translate.get('settings.sms.outbound')}
                    </div>
                    <div className="button-group">
                      <button
                        disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                        className={'option-button ' +
                          ((props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.outboundNotificationsFor`) === 'SmsDetail')
                            ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                            updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.outboundNotificationsFor`, 'SmsDetail')
                        }}
                      >
                        {Translate.get('settings.sms.smsDetail')}
                      </button>
                      <button
                        disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                        className={'option-button ' +
                          ((props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.outboundNotificationsFor`) === 'MainPageOnly' ||
                            !props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.outboundNotificationsFor`))
                            ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                            updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.outboundNotificationsFor`, 'MainPageOnly')
                        }}
                      >
                        {Translate.get('settings.sms.mainPageOnly')}
                      </button>
                      <button
                        disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                        className={'option-button ' +
                          (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.outboundNotificationsFor`) === 'Off'
                            ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                              ? 'selected'
                              : 'selected-silent')
                            : '')
                        }
                        onClick={() => {
                          props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                            updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.outboundNotificationsFor`, 'Off')
                        }}
                      >
                        {Translate.get('settings.sms.off')}
                      </button>
                    </div>
                  </div>

                  <div className="notification-group-label">
                    {Translate.get('settings.sms.createSmsActivityFor')}
                  </div>
                  <div className="notification-group">
                    <div className="activities-grid">
                      <div
                        className={'activity-item' + (!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) ? ' disabled' : '')}
                      >
                        <div className='activity-control'>
                          <label className="switch-container small">
                            <input
                              id={`${device.deviceId}_inbound`}
                              type="checkbox"
                              className='switch-input'
                              checked={props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound`)}
                              disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                              onChange={() => {
                                if (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)) {
                                  const value = props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound`)
                                  if (value) {
                                    updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound`, undefined)
                                  }
                                  else {
                                    updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound`, { markAsDone: false })
                                  }
                                }
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <label
                            htmlFor={`${device.deviceId}_inbound`}
                            className="activity-label"
                            disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                          >
                            {Translate.get('settings.calls.inbound')}
                          </label>
                        </div>

                        {(props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound`)) &&

                          <div
                            className='activity-control'
                            onClick={() => props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                              toggleCreateActivityForDone(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound`)}
                          >
                            <div className={'checkbox ' +
                              (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound.markAsDone`)
                                ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                                  ? 'checked'
                                  : 'checked-silent')
                                : 'unchecked')
                            }>
                              {props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound.markAsDone`) &&
                                <Check size={14} color={props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.inbound.markAsDone`) ? 'white' : '#a5a5a5'} />
                              }
                            </div>
                            <label className="activity-label extended">
                              {Translate.get('settings.calls.markAsDone')}
                            </label>
                          </div>
                        }
                      </div>
                      <div
                        className={'activity-item' + (!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) ? ' disabled' : '')}
                      >
                        <div className='activity-control'>
                          <label className="switch-container small">
                            <input
                              id={`${device.deviceId}_outbound`}
                              type="checkbox"
                              className='switch-input'
                              checked={props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound`)}
                              disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                              onChange={() => {
                                if (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)) {
                                  const value = props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound`)
                                  if (value) {
                                    updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound`, undefined)
                                  }
                                  else {
                                    updateSettings(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound`, { markAsDone: true })
                                  }
                                }
                              }}
                            />
                            <span className="switch-slider"></span>
                          </label>
                          <label
                            htmlFor={`${device.deviceId}_outbound`}
                            className="activity-label"
                            disabled={!props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)}
                          >
                            {Translate.get('settings.calls.outbound')}
                          </label>
                        </div>

                        {(props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound`)) &&

                          <div
                            className='activity-control'
                            onClick={() => props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`) &&
                              toggleCreateActivityForDone(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound`)}
                          >
                            <div className={'checkbox ' +
                              (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound.markAsDone`)
                                ? (props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.isEnabled`)
                                  ? 'checked'
                                  : 'checked-silent')
                                : 'unchecked')
                            }>
                              {props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound.markAsDone`) &&
                                <Check size={14} color={props.settingsData.get(`.smsSettings.smsGatewaySettings.${device.deviceId}.createSmsActivityFor.outbound.markAsDone`) ? 'white' : '#a5a5a5'} />
                              }
                            </div>
                            <label className="activity-label extended">
                              {Translate.get('settings.calls.markAsDone')}
                            </label>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>) : <LoadingCircle />
          )}

          {activeTab === 'other' && (
            <div>
              <div className="form-group">
                <label className="form-label">
                  {Translate.get('settings.other.notificationDisplayTime')}
                </label>
                <input
                  type="text"
                  className="settings-input"
                  value={notificationDisplayTime}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value === '' || parseInt(value) <= 43200) {
                      setNotificationDisplayTime(value);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (!value) setNotificationDisplayTime(30);
                    if (value === '' || parseInt(value) <= 43200) {
                      updateSettings('.notificationDisplayTime', value || 30);
                    }
                  }}
                />
                <p className="settings-help">
                  {Translate.get('settings.other.notificationDisplayTimeHelp')}
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {Translate.get('settings.other.language')}
                </label>
                <select
                  className="form-select"
                  value={props.settingsData.get('.language') || 'en'}
                  onChange={(e) => {
                    updateSettings('.language', e.target.value)
                    Translate.setLanguage(e.target.value)
                  }}
                >
                  <option value="en">
                    {Translate.get('settings.other.english')}
                  </option>
                  <option value="cs">
                    {Translate.get('settings.other.czech')}
                  </option>
                </select>
              </div>

              <div className='disconnect-section'>
                <label className="form-label">
                  {Translate.get('settings.other.connectionInfo.tenant')} <strong>{props.settingsData.get('._tenant')}</strong><br />
                  {Translate.get('settings.other.connectionInfo.user')} <strong>{props.settingsData.get('._user')}</strong>
                </label>
                <button
                  className="logout-button"
                  onClick={handleLogout}
                >
                  {Translate.get('settings.other.logout.button')}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'about' && (
            <div className='settings-about'>
              <div className='settings-about-header'>OptimCall Pipedrive App</div>
              <div className='settings-about-body'>
                <a 
                  className='settings-about-link'
                  href='https://www.optimcall.cz/integrace/terms-of-service-for-the-optimcall-pipedrive-app'
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {Translate.get('settings.about.termsOfService')}
                </a>
                <a 
                  className='settings-about-link'
                  href='https://www.optimcall.cz/integrace/privacy-policy-for-the-optimcall-pipedrive-app'
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {Translate.get('settings.about.privacyPolicy')}
                </a>
              </div>
              <div className='settings-about-body'>
                <div className='settings-about-row'>
                  <div>{Translate.get('settings.about.version')}</div>
                  <div className='settings-about-app'>1.8831</div>
                </div>
                <div className='settings-about-app'>Mon Sep 14 07:52:40 AM CEST 2026</div>
                <div className='settings-about-row'>
                  <div>{Translate.get('settings.about.createdBy')}</div>
                  <div className='settings-about-app'>OptimSys, s.r.o.</div>
                </div>
              </div>
              <div className='settings-about-body'>
                <a 
                  className='settings-about-link'
                  href={'mailto:' + Translate.get('settings.about.supportEmail')}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {Translate.get('settings.about.supportEmail')}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;